import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { ImportHistoryApiAdapter } from '../adapters/ImportHistoryApiAdapter';
import type { ImportHistoryFilters } from '../adapters/ImportHistoryApiAdapter';

const defaultFilters: ImportHistoryFilters = {
  sort_column: 'id',
  sort_direction: 'desc',
  per_page: 15,
};

export const useImportHistory = (
  filters: Partial<ImportHistoryFilters> & {
    searchTerm?: string;
    filterConditions?: FilterConditionOutput[];
  } = {},
) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new ImportHistoryApiAdapter(httpClient);

  // 🎯 Procesar filtros para usar advanced_filters
  const processedFilters = useMemo(() => {
    const { searchTerm = '', filterConditions = [], ...restFilters } = filters;

    const advancedFilters: ImportHistoryFilters['advanced_filters'] =
      filterConditions.length > 0
        ? {
            filters: filterConditions.map((condition) => ({
              column: condition.column,
              operator: condition.action,
              value: condition.value,
            })),
            logic: 'AND' as const,
          }
        : undefined;

    return {
      ...defaultFilters,
      ...restFilters,
      searchTerm,
      advanced_filters: advancedFilters,
    };
  }, [filters]);

  const mergedFilters = processedFilters;

  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching, isPending } =
    useInfiniteQuery({
      queryKey: ['shops-import-history', mergedFilters],
      queryFn: ({ pageParam = 1 }) =>
        apiAdapter.getImportHistoryPaginated({
          ...mergedFilters,
          page: pageParam,
        }),
      getNextPageParam: (lastPage) => lastPage.nextPage,
      initialPageParam: 1,
      placeholderData: (previousData) => previousData,
      staleTime: 2 * 60 * 1000,
    });

  const items = data?.pages.flatMap((page) => page.items) ?? [];
  const total = data?.pages?.[0]?.total ?? 0;

  return {
    items,
    isLoading: isLoading || isPending,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    total,
  };
};
